import React, { useEffect, useState } from "react";
import { PaymentForm, CreditCard, ApplePay } from "react-square-web-payments-sdk";
import { createSquarePayment } from "../services/apis";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
interface SquarePaymentProps {
  pricing: number;
  handleOrder: (
    paymentMethod: string,
    userId?: string,
    transactionId?: string,
  ) => void;
  onSuccess?: () => void;
  applicationId?: string | null;
  locationId?: string | null;
  hotelId?: string;
}

export default function SquarePayment({
  pricing,
  handleOrder,
  onSuccess,
  applicationId: applicationIdProp,
  locationId: locationIdProp,
  hotelId,
}: SquarePaymentProps) {
  const [loading, setLoading] = useState(false);
  const { squareApplicationId, squareLocationId } = useAuth();
  const applicationId = applicationIdProp ?? squareApplicationId;
  const locationId = locationIdProp ?? squareLocationId;

  useEffect(() => {
    console.log("Square credentials updated:", {
      applicationId,
      locationId,
    });
  }, [applicationId, locationId]);

  const paymentAmountCents = Math.round(pricing * 100);
  const currencyCode = "USD";
  const countryCode = "US";
  const paymentLabel = "Order Total";

  return (
    <div className="w-full bg-white dm-payment-section rounded-lg rounded-tl-none rounded-tr-none border border-black/10 border-t-0 p-4 sm:p-5">
      <PaymentForm
        key="square-payment-form"
        applicationId={applicationId!.trim()}
        locationId={locationId!.trim()}
        createPaymentRequest={() => ({
          countryCode,
          currencyCode,
          total: {
            amount: (paymentAmountCents / 100).toFixed(2),
            label: paymentLabel,
          },
        })}
        cardTokenizeResponseReceived={async (tokenResult) => {
          console.log("Token Result:", tokenResult);
          const details = (tokenResult as any).details;
          const isApplePay = details?.method === "Apple Pay";
          const paymentMethod = isApplePay ? "apple-pay" : "square";
          if (tokenResult.status !== "OK") {
            toast.error("Card tokenization failed. Please try again.");
            return;
          }

          try {
            setLoading(true);

            const res = await createSquarePayment({
              token: tokenResult.token,
              amount: paymentAmountCents,
              ...(hotelId && { hotelId }),
            });

            console.log("Payment Response:", res.data);
            if (res.data.success) {
              const transactionId = res.data?.data?.payment?.id;
              console.log(
                "🔵 SquarePayment - transactionId extracted:",
                transactionId,
              );
              console.log(
                "🔵 SquarePayment - full payment object:",
                res.data?.payment,
              );
              toast.success("Payment successful!");
              handleOrder(paymentMethod, undefined, transactionId);
              console.log(
                "🔵 SquarePayment - handleOrder called with transactionId:",
                transactionId,
              );
              onSuccess?.();
            } else {
              toast.error(
                "Payment failed: " + (res.data.error || "Unknown error"),
              );
            }
          } catch (err: any) {
            console.error("Payment error:", err);
            toast.error(
              err.response?.data?.error || "Payment processing failed",
            );
          } finally {
            setLoading(false);
          }
        }}
      >
        <ApplePay />
        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-sm text-gray-400">or pay with card</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>
        <CreditCard
          includeInputLabels
          buttonProps={{
            css: {
              backgroundColor: "var(--main-color, #000)",
              color: "#fff",
              fontSize: "15px",
              fontWeight: "600",
              border: "none",
              borderRadius: "0.375rem",
              padding: "0.75rem",
              cursor: "pointer",
              width: "100%",
              marginTop: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              "&:hover": {
                backgroundColor: "var(--main-color, #000)",
                opacity: 0.95,
              },
              "&:disabled": {
                backgroundColor: "#9ca3af",
                cursor: "not-allowed",
              },
            },
          }}
        >
          {loading ? (
            <>
              <svg
                style={{
                  width: "1.25rem",
                  height: "1.25rem",
                  animation: "spin 1s linear infinite",
                  color: "white",
                }}
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Processing...
            </>
          ) : (
            `PAY ${pricing.toFixed(2)}`
          )}
        </CreditCard>
      </PaymentForm>

      {/* CSS required for Square's iframes - cannot use Tailwind as these are rendered in iframes */}
      <style>{`
        /* Spinner animation */
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* Square SDK renders payment fields inside iframes, so we must use CSS with specific IDs */
        /* These selectors target the iframe content which Tailwind classes cannot reach */

        #sq-card-number,
        #sq-cvv,
        #sq-expiration-date,
        #sq-postal-code {
          background: white !important;
          border: 1px solid rgba(0, 0, 0, 0.15) !important;
          border-radius: 8px !important;
          padding: 14px !important;
          font-size: 15px !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
          height: 50px !important;
        }

        .theme-dark #sq-card-number,
        .theme-dark #sq-cvv,
        .theme-dark #sq-expiration-date,
        .theme-dark #sq-postal-code {
          background: #374151 !important;
          border-color: rgba(255, 255, 255, 0.2) !important;
          color: white !important;
        }

        #sq-card-number:focus,
        #sq-cvv:focus,
        #sq-expiration-date:focus,
        #sq-postal-code:focus {
          border-color: var(--main-color, #000) !important;
          box-shadow: 0 0 0 1px var(--main-color, #000) !important;
          outline: none !important;
        }

        .sq-input--exp,
        .sq-input--cvv,
        .sq-input--postal {
          display: inline-block !important;
          width: calc(33.333% - 8px) !important;
          margin-right: 12px !important;
        }

        .sq-input--postal {
          margin-right: 0 !important;
        }

        .sq-label {
          color: #4b5563 !important;
          font-size: 13px !important;
          font-weight: 600 !important;
          margin-bottom: 8px !important;
          display: block !important;
        }

        .theme-dark .sq-label {
          color: #d1d5db !important;
        }

        .sq-error-message {
          color: #ef4444 !important;
          font-size: 12px !important;
          margin-top: 6px !important;
          font-weight: 500 !important;
        }

        .sq-input {
          margin-bottom: 18px !important;
        }

        .sq-input--card-number {
          margin-bottom: 18px !important;
        }

        @media (max-width: 640px) {
          .sq-input--exp,
          .sq-input--cvv,
          .sq-input--postal {
            width: 100% !important;
            margin-right: 0 !important;
          }
        }
      `}</style>
    </div>
  );
}
